import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Instagram, Facebook, Twitter, Youtube } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const SOCIAL = [Instagram, Facebook, Twitter, Youtube];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const submit = (e) => {
    e.preventDefault();
    toast.success("Thanks! Our team will get back to you shortly.");
    setForm({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-3">Get in touch</p>
        <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter text-neutral-900">Contact us.</h1>
        <p className="mt-4 text-neutral-600 max-w-xl">Questions, feedback or partnership ideas? We'd love to hear from you.</p>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-12 mt-12">
        <div className="space-y-6">
          <a href="tel:0685973458" className="flex items-center gap-4 border border-neutral-200 bg-white p-5 hover:border-[#d00000] transition-colors" data-testid="contact-phone">
            <span className="w-11 h-11 bg-[#d00000] flex items-center justify-center"><Phone className="w-5 h-5 text-white" /></span>
            <div><p className="text-xs uppercase tracking-widest text-neutral-400">Phone</p><p className="text-neutral-900 font-medium">0685973458</p></div>
          </a>
          <a href="mailto:admin.ken@jollanda.com" className="flex items-center gap-4 border border-neutral-200 bg-white p-5 hover:border-[#d00000] transition-colors" data-testid="contact-email-admin">
            <span className="w-11 h-11 bg-[#d00000] flex items-center justify-center"><Mail className="w-5 h-5 text-white" /></span>
            <div><p className="text-xs uppercase tracking-widest text-neutral-400">Admin</p><p className="text-neutral-900 font-medium">admin.ken@jollanda.com</p></div>
          </a>
          <a href="mailto:customersupport@jollanda.com" className="flex items-center gap-4 border border-neutral-200 bg-white p-5 hover:border-[#d00000] transition-colors" data-testid="contact-email-support">
            <span className="w-11 h-11 bg-[#d00000] flex items-center justify-center"><Mail className="w-5 h-5 text-white" /></span>
            <div><p className="text-xs uppercase tracking-widest text-neutral-400">Customer support</p><p className="text-neutral-900 font-medium">customersupport@jollanda.com</p></div>
          </a>
          <div className="flex items-center gap-4 border border-neutral-200 bg-white p-5">
            <span className="w-11 h-11 bg-neutral-900 flex items-center justify-center"><MapPin className="w-5 h-5 text-white" /></span>
            <div><p className="text-xs uppercase tracking-widest text-neutral-400">Head office</p><p className="text-neutral-900 font-medium">Amsterdam, Netherlands</p></div>
          </div>
          <div className="flex gap-3 pt-2">
            {SOCIAL.map((Icon, i) => (
              <a key={i} href="#" className="w-11 h-11 border border-neutral-300 flex items-center justify-center text-neutral-500 hover:text-white hover:bg-[#d00000] hover:border-[#d00000] transition-colors" aria-label="social">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5 border border-neutral-200 bg-white p-8" data-testid="contact-form">
          <h2 className="font-display text-2xl font-bold text-neutral-900">Send a message</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-neutral-500 text-xs uppercase tracking-widest">Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" className="mt-2 h-12 bg-[#fbf9f7] border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
            </div>
            <div>
              <Label className="text-neutral-500 text-xs uppercase tracking-widest">Email</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" className="mt-2 h-12 bg-[#fbf9f7] border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
            </div>
          </div>
          <div>
            <Label className="text-neutral-500 text-xs uppercase tracking-widest">Subject</Label>
            <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" className="mt-2 h-12 bg-[#fbf9f7] border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
          </div>
          <div>
            <Label className="text-neutral-500 text-xs uppercase tracking-widest">Message</Label>
            <Textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} data-testid="contact-message" className="mt-2 bg-[#fbf9f7] border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
          </div>
          <Button type="submit" data-testid="contact-submit" className="bg-[#d00000] hover:bg-[#a30000] text-white rounded-none h-12 px-8">Send message</Button>
        </form>
      </div>
    </div>
  );
}
